import * as React from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Box, Button, Confetti, useToggle } from '@factorialco/deprecated-design-system'
import { PayrollBundleInHouseIncidencePageDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSearchParams from 'modules/core/hooks/useSearchParams'

import { IncidenceMessageStatsProvider } from 'modules/bookkeepers/hooks/IncidenceMessageStatsContext'
import { IncidenceStatsProvider } from 'modules/bookkeepers/hooks/IncidenceStatsContext'
import { IncidencesProvider } from 'modules/bookkeepers/hooks/IncidencesContext'
import useGetIncidences from 'modules/bookkeepers/hooks/useGetIncidences'
import { useMessageFilterContext } from 'modules/bookkeepers/lib/MessageFilterContext'
import ExportDataButton from 'modules/payroll_bundle/components/PageHeader/PayrollExportButton'
import useBulkIncidenceStatusChange from 'modules/payroll_bundle/hooks/useBulkIncidenceStatusChange'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { ElectiveIncidenceStatus } from 'modules/payroll_bundle/lib/Incidences/Status'
import ActionsMenu from 'modules/payroll_bundle/pages/IncidencesPage/components/_ActionsMenu'
import FiltersBar from 'modules/payroll_bundle/pages/IncidencesPage/components/_FiltersBar'
import IncidencesPageBody from 'modules/payroll_bundle/pages/IncidencesPage/components/_IncidencesPageBody'
import IncidencesPageHeader from 'modules/payroll_bundle/pages/IncidencesPage/components/_IncidencesPageHeader'
import IncidencesTable from 'modules/payroll_bundle/pages/IncidencesPage/components/_IncidencesTable'
import NewManualIncidenceModal from 'modules/payroll_bundle/pages/IncidencesPage/components/_ManualIncidenceModal/_New'
import { StatusChangeDialog } from 'modules/payroll_bundle/pages/IncidencesPage/components/_StatusChangeDialog'

const InHouseIncidencesPage = () => {
  const { id } = useParams()
  const incidenceDetail = <Outlet />
  const selectedIncidenceId = Number(id)
  const messageFilters = useMessageFilterContext()
  const searchParams = useSearchParams()
  const inhouseActivated = Boolean(searchParams.activated)
  const { canDownloadPayrollData } = usePayrollContextlessPolicies()

  const [showConfetti, setShowConfetti] = React.useState(false)

  useEffect(() => {
    if (inhouseActivated) {
      setTimeout(() => {
        setShowConfetti(true)
      }, 800)
    }
  }, [inhouseActivated])

  const {
    filters: mFilters,
    debouncedSearch: mDebouncedSearch,
    filterDate: mFilterDate,
  } = messageFilters
  const { loading, incidences, refetch } = useGetIncidences()
  const [selectedIncidencesId, setSelectedIncidencesId] = React.useState<Set<number>>(new Set())

  const { bulkChangeIncidenceStatuses, updatingStatusBulk } = useBulkIncidenceStatusChange({
    onStatusChanged() {
      setSelectedIncidencesId(new Set())
    },
  })

  const confirmStatusChange = (newStatus: ElectiveIncidenceStatus) => {
    switch (newStatus) {
      case 'discarded':
        return setConfirmChangeToStatus('discarded')
      case 'doing':
        return setConfirmChangeToStatus('doing')
      case 'to-do':
        return setConfirmChangeToStatus('to-do')
      case 'done':
        return setConfirmChangeToStatus('done')
    }
  }

  const legalEntityIds = mFilters.legal_entity_id ? [Number(mFilters.legal_entity_id)] : undefined
  const { data: incidenceStatsData } = useQuery(PayrollBundleInHouseIncidencePageDocument, {
    variables: {
      type: mFilters.name?.split(','),
      status: mFilters.status?.split(','),
      startsOn: mFilterDate.firstDate,
      endsOn: mFilterDate.lastDate,
      search: mDebouncedSearch,
      legalEntitiesIds: legalEntityIds,
    },
    fetchPolicy: 'network-only',
  })
  const incidenceStats =
    incidenceStatsData?.bookkeepersManagement.incidenceStatsConnection.nodes ?? []

  const [open, toggleModal] = useToggle(false)
  const handleClose = async () => {
    await Promise.all([refetch()])
    const closeModal = toggleModal(false)
    closeModal()
  }

  const [confirmChangeToStatus, setConfirmChangeToStatus] =
    React.useState<ElectiveIncidenceStatus | null>(null)
  const selectedIncidences = useUnsafeMemo(() => {
    return incidences.filter((incidence) => selectedIncidencesId.has(incidence.id))
  }, [incidences, selectedIncidencesId])

  const dismissStatusChangeDialog = () => setConfirmChangeToStatus(null)

  const inHouseIncidenceTable = (
    <Box position="relative">
      {open && <NewManualIncidenceModal onClose={handleClose} />}

      <Box
        paddingBottom="s8"
        flexDirection="row"
        justifyContent="flexEnd"
        alignItems="center"
        gap="s8"
      >
        {confirmChangeToStatus && (
          <StatusChangeDialog
            incidences={selectedIncidences}
            newStatus={confirmChangeToStatus}
            onDismiss={dismissStatusChangeDialog}
            onCommit={(confirmedIncidenceIds) => {
              const confirmedIncidences = selectedIncidences.filter((i) =>
                confirmedIncidenceIds.has(i.id)
              )
              return bulkChangeIncidenceStatuses(confirmedIncidences, confirmChangeToStatus)
            }}
          />
        )}
      </Box>
      <Box flexDirection="row" gap="s8" flexGrow alignItems="center" width="full">
        <FiltersBar messageTab={false} collapsibleSearchBar />
        <ActionsMenu
          onSelectNewStatus={confirmStatusChange}
          isLoading={updatingStatusBulk}
          selectedElements={selectedIncidences}
        />
        {canDownloadPayrollData && <ExportDataButton />}
        <Button iconName="add" type="secondary" onClick={toggleModal(true)}></Button>
      </Box>
      <IncidencesTable
        incidences={incidences}
        isLoading={loading}
        isMessageTab={false}
        selectedIncidencesId={selectedIncidencesId}
        setSelectedIncidencesId={setSelectedIncidencesId}
      />
    </Box>
  )

  return (
    <IncidencesProvider value={incidences}>
      <IncidenceStatsProvider value={incidenceStats}>
        <IncidenceMessageStatsProvider value={[]}>
          <IncidencesPageHeader />
          <IncidencesPageBody
            incidenceDetail={incidenceDetail}
            selectedIncidenceId={selectedIncidenceId}
            loading={loading}
            incidencesTable={inHouseIncidenceTable}
          />
          {showConfetti && <Confetti />}
        </IncidenceMessageStatsProvider>
      </IncidenceStatsProvider>
    </IncidencesProvider>
  )
}

export default InHouseIncidencesPage
